import {ApplicableCopy, ClaimState, PromoApplied, PromoCardData} from './types'

const DAY = 86_400_000
const EXPIRY_WARN_DAYS = 3

export type CardBucket = 'expired' | 'ready' | 'used'
export type CardUiState = 'expired' | 'paused' | 'ready' | 'soon' | 'used'

// The list a card came from wins over a stale `state`; a ready card whose expiresAt has passed
// reads as expired so a cached list never offers a dead card.
export const cardUiState = (
  card: Pick<PromoCardData, 'expiresAt' | 'state'>,
  bucket?: CardBucket,
  now: number = Date.now(),
): CardUiState => {
  if (bucket === 'used') {
    return 'used'
  }
  if (bucket === 'expired') {
    return 'expired'
  }
  const s = (card.state ?? '').toUpperCase()
  if (s === 'USED') {
    return 'used'
  }
  if (s === 'EXPIRED' || s === 'REMOVED' || s === 'ENDED') {
    return 'expired'
  }
  if (card.expiresAt && card.expiresAt <= now) {
    return 'expired'
  }
  if (s === 'NOT_STARTED' || s === 'SCHEDULED') {
    return 'soon'
  }
  if (s === 'PAUSED' || s === 'INACTIVE') {
    return 'paused'
  }
  return 'ready'
}

export const isExpiringSoon = (
  card: Pick<PromoCardData, 'expiresAt' | 'state'>,
  bucket?: CardBucket,
  now: number = Date.now(),
): boolean => {
  if (cardUiState(card, bucket, now) !== 'ready' || !card.expiresAt) {
    return false
  }
  const ms = card.expiresAt - now
  return ms > 0 && ms < EXPIRY_WARN_DAYS * DAY
}

// LSC07K3P7Q2M -> "LSC 07 K3P7Q2M" (display only). Short or typed codes come back untouched.
export const groupCode = (code: string): string => {
  const raw = code.replace(/\s+/g, '').toUpperCase()
  const m = raw.match(/^([A-Z]{2,4})(.*)$/)
  if (!m || m[2].length < 9) {
    return raw
  }
  const number = m[2].slice(0, -7)
  return /^\d{2,}$/.test(number) ? `${m[1]} ${number} ${m[2].slice(-7)}` : raw
}

const bare = (code: string | null | undefined): string | null => (code ?? '').replace(/\s+/g, '').toUpperCase() || null

export const applicableKey = (c: Pick<ApplicableCopy, 'copyId' | 'promoCodeId'>): string =>
  c.copyId || c.promoCodeId || ''

// One list from several priced options (two sessions quote twice): savings add up per promotion;
// it only works if it works on every option, so any reason sticks.
export const mergeApplicable = (lists: (ApplicableCopy[] | null | undefined)[]): ApplicableCopy[] => {
  const byKey = new Map<string, ApplicableCopy>()
  lists.forEach((list) => {
    (list ?? []).forEach((c) => {
      const key = applicableKey(c)
      if (!key) {
        return
      }
      const have = byKey.get(key)
      byKey.set(key, have ? {...have, saves: have.saves + c.saves, reason: have.reason ?? c.reason} : {...c})
    })
  })
  return Array.from(byKey.values())
}

// The code to apply for a quote entry: the held card's, else the one the entry carried.
export const codeForApplicable = (c: ApplicableCopy, ready: PromoCardData[]): string | null => {
  const card = ready.find(
    (r) => (c.copyId && r.copyId === c.copyId) || (c.promoCodeId && r.promoCodeId === c.promoCodeId),
  )
  return bare(card?.code) ?? bare(c.code)
}

export const isApplicableApplied = (c: ApplicableCopy, applied: PromoApplied | null, ready: PromoCardData[]) => {
  if (!applied) {
    return false
  }
  if (c.copyId && applied.copyId === c.copyId) {
    return true
  }
  const code = codeForApplicable(c, ready)
  return Boolean(code) && bare(applied.code) === code
}

// The promotion to pre-select: the biggest saving among those that work here and have a code.
export const pickBest = (
  applicable: ApplicableCopy[],
  ready: PromoCardData[],
): {entry: ApplicableCopy; code: string; key: string} | null => {
  let best: {entry: ApplicableCopy; code: string; key: string} | null = null
  applicable.forEach((entry) => {
    if (entry.reason || !(entry.saves > 0)) {
      return
    }
    const code = codeForApplicable(entry, ready)
    if (code && (!best || entry.saves > best.entry.saves)) {
      best = {entry, code, key: applicableKey(entry)}
    }
  })
  return best
}

const SCOPE = {
  class: {all: 'allClasses', ids: 'classIds'},
  service: {all: 'allServices', ids: 'serviceIds'},
  subscription: {all: 'allSubscriptions', ids: 'subscriptionIds'},
  product: {all: 'allProducts', ids: 'stockItemIds'},
} as const

// Does a held card work on this item? A card with no `appliesTo` says nothing, so: no.
export const cardAppliesTo = (
  card: Pick<PromoCardData, 'appliesTo'>,
  item: {entityType: keyof typeof SCOPE; entityId: string},
): boolean => {
  const a = card.appliesTo
  if (!a) {
    return false
  }
  const s = SCOPE[item.entityType]
  return a[s.all] === true || (a[s.ids] ?? []).includes(item.entityId)
}

// The server lets a customer save a promotion that has not started yet (exsportia-server#3603).
export const canSaveClaim = (state: ClaimState) => state === 'READY' || state === 'NOT_STARTED'

export const claimCardState = (state: ClaimState, hasNotice: boolean): CardUiState => {
  if (state === 'NOT_STARTED') {
    return 'soon'
  }
  return hasNotice && state !== 'ALREADY_HAVE' ? 'expired' : 'ready'
}
